import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, Grid, Link, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Tooltip, Typography,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AdminLayout from '../../layouts/AdminLayout';
import { AdminService } from '../../services/adminService';

/**
 * Les Grimoires imprimés chez Cloudprinter (`services/impression/`) : où en est chaque commande,
 * et la reprise à la main de ce qui coince (les alertes envoyées à l'équipe pointent ici,
 * `?commande=<id>`). Tout est décidé par le serveur ; l'écran affiche et transmet.
 * 🔴 Aucun remboursement ici : il se fait dans Stripe (Marc, 2026-10-14).
 */

const STATUTS = {
  a_envoyer: { libelle: 'À envoyer', couleur: 'default' },
  envoyee: { libelle: 'Chez l’imprimeur', couleur: 'info' },
  en_production: { libelle: 'En fabrication', couleur: 'info' },
  expediee: { libelle: 'Expédiée', couleur: 'primary' },
  livree: { libelle: 'Livrée', couleur: 'success' },
  erreur: { libelle: 'Erreur', couleur: 'error' },
  annulee: { libelle: 'Annulée', couleur: 'warning' },
  echec_livraison: { libelle: 'Échec de livraison', couleur: 'error' },
};
const ORDRE = ['erreur', 'echec_livraison', 'a_envoyer', 'envoyee', 'en_production', 'expediee', 'livree', 'annulee'];
const CHAMPS_ADRESSE = [
  ['prenom', 'Prénom'], ['nom', 'Nom'], ['ligne1', 'Adresse'], ['ligne2', 'Complément'],
  ['codePostal', 'Code postal'], ['ville', 'Ville'], ['pays', 'Pays (FR, BE, LU, MC)'],
  ['telephone', 'Téléphone'], ['email', 'Email'],
];

const date = (iso) => (iso ? new Date(iso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : '—');
const euros = (c) => (c == null ? '—' : `${(c / 100).toFixed(2).replace('.', ',')} €`);
const message = (e, defaut) => e?.response?.data?.error || defaut;
/** Le message d'une erreur de flux (`apiRequestStream` : le corps JSON brut du serveur). */
const messageDeFlux = (e, defaut) => {
  try { return JSON.parse(e.message).error || defaut; } catch { return e?.message || defaut; }
};

function PuceStatut({ statut }) {
  const s = STATUTS[statut] || { libelle: statut, couleur: 'default' };
  return <Chip size="small" label={s.libelle} color={s.couleur} />;
}

function FormulaireAdresse({ valeur, onChange }) {
  return (
    <Grid container spacing={1}>
      {CHAMPS_ADRESSE.map(([cle, libelle]) => (
        <Grid item xs={12} sm={cle === 'ligne1' || cle === 'ligne2' || cle === 'email' ? 12 : 6} key={cle}>
          <TextField
            size="small" fullWidth label={libelle} value={valeur?.[cle] || ''}
            onChange={(e) => onChange({ ...valeur, [cle]: e.target.value })}
          />
        </Grid>
      ))}
    </Grid>
  );
}

/** Une demande de texte (raison, motif) avant une action, et éventuellement une adresse. */
function DialogueAction({ ouvert, titre, explication, libelleTexte, texteObligatoire, avecAdresse, adresseInitiale, onFermer, onValider }) {
  const [texte, setTexte] = useState('');
  const [adresse, setAdresse] = useState(null);
  const [changerAdresse, setChangerAdresse] = useState(false);
  useEffect(() => {
    if (ouvert) { setTexte(''); setAdresse(adresseInitiale || {}); setChangerAdresse(!adresseInitiale); }
  }, [ouvert, adresseInitiale]);
  return (
    <Dialog open={ouvert} onClose={onFermer} maxWidth="sm" fullWidth>
      <DialogTitle>{titre}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>{explication}</Typography>
        <TextField fullWidth multiline minRows={2} label={libelleTexte} value={texte} onChange={(e) => setTexte(e.target.value)} sx={{ mb: 2 }} />
        {avecAdresse ? (
          <>
            {adresseInitiale ? (
              <Button size="small" onClick={() => setChangerAdresse((v) => !v)} sx={{ mb: 1 }}>
                {changerAdresse ? 'Garder l’adresse d’origine' : 'Livrer à une autre adresse'}
              </Button>
            ) : null}
            {changerAdresse ? <FormulaireAdresse valeur={adresse} onChange={setAdresse} /> : null}
          </>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onFermer}>Fermer</Button>
        <Button
          variant="contained" disabled={texteObligatoire && !texte.trim()}
          onClick={() => onValider(texte.trim(), avecAdresse && changerAdresse ? adresse : null)}
        >
          Valider
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function DetailCommande({ id, onFermer, onChange }) {
  const [c, setC] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [info, setInfo] = useState(null);
  const [occupe, setOccupe] = useState(false);
  const [adresseEditee, setAdresseEditee] = useState(null);
  const [dialogue, setDialogue] = useState(null); // 'annuler' | 'refaire'

  const charger = useCallback(async () => {
    try {
      setC(await AdminService.getImpression(id));
      setErreur(null);
    } catch (e) {
      setErreur(message(e, 'Lecture impossible'));
    }
  }, [id]);
  useEffect(() => { setC(null); setInfo(null); setAdresseEditee(null); charger(); }, [charger]);

  const agir = async (fn, apres) => {
    setOccupe(true);
    setErreur(null);
    setInfo(null);
    try {
      const r = await fn();
      const commande = r?.commande || r;
      if (commande?.id && String(commande.id) === String(id)) setC(commande);
      if (apres) setInfo(apres(r));
      onChange();
      return true;
    } catch (e) {
      setErreur(message(e, 'Opération impossible'));
      return false;
    } finally {
      setOccupe(false);
    }
  };

  /** Le PDF tel que Cloudprinter le reçoit (fond perdu) : celui parti, ou fabriqué s'il ne l'est pas encore. */
  const telecharger = async () => {
    setOccupe(true);
    setErreur(null);
    setInfo('Préparation du PDF : jusqu’à une minute s’il n’a pas encore été fabriqué.');
    try {
      const { blob, filename } = await AdminService.telechargerFichierImpression(id);
      const url = URL.createObjectURL(blob);
      const lien = document.createElement('a');
      lien.href = url;
      lien.download = filename;
      lien.click();
      URL.revokeObjectURL(url);
      setInfo(null);
      charger();
    } catch (e) {
      setInfo(null);
      setErreur(messageDeFlux(e, 'Téléchargement impossible'));
    } finally {
      setOccupe(false);
    }
  };

  return (
    <Dialog open onClose={onFermer} maxWidth="md" fullWidth>
      <DialogTitle>
        {c ? <>Commande {c.reference} <PuceStatut statut={c.statut} /></> : 'Commande'}
      </DialogTitle>
      <DialogContent dividers>
        {erreur ? <Alert severity="error" sx={{ mb: 2 }}>{erreur}</Alert> : null}
        {info ? <Alert severity="info" sx={{ mb: 2 }}>{info}</Alert> : null}
        {!c ? <CircularProgress /> : (
          <>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2"><b>Enfant</b> : {c.prenomEnfant || '—'} · <b>achat</b> {c.achatId}</Typography>
                <Typography variant="body2"><b>Acheteur</b> : {c.acheteurEmail || '—'}</Typography>
                <Typography variant="body2"><b>Payé</b> : {euros(c.montantCentimes)} le {date(c.payeeLe)}</Typography>
                <Typography variant="body2"><b>Coût Cloudprinter</b> : {euros(c.coutHtCentimes)} HT ({c.niveauLivraison || '—'}, {c.nbPages || '?'} p.)</Typography>
                {c.refaitDe ? <Typography variant="body2"><b>Exemplaire refait</b> de la commande {c.refaitDe} : {c.motifRefait}</Typography> : null}
                {c.refaits?.length ? (
                  <Typography variant="body2"><b>Refait en</b> : {c.refaits.map((r) => `${r.reference} (${STATUTS[r.statut]?.libelle || r.statut})`).join(', ')}</Typography>
                ) : null}
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2"><b>Créée</b> {date(c.creeLe)} · <b>envoyée</b> {date(c.envoyeeLe)}</Typography>
                <Typography variant="body2"><b>Expédiée</b> {date(c.expedieeLe)} · <b>finie</b> {date(c.termineeLe)}</Typography>
                <Typography variant="body2"><b>Dernier signal</b> : {c.dernierSignal || '—'} ({date(c.dernierSignalLe)})</Typography>
                <Typography variant="body2">
                  <b>Suivi</b> : {c.suivi ? (c.suiviUrl ? <Link href={c.suiviUrl} target="_blank" rel="noopener">{c.suivi}</Link> : c.suivi) : '—'}
                  {c.transporteur ? ` (${c.transporteur})` : ''} · mail {c.mailExpeditionLe ? `envoyé ${date(c.mailExpeditionLe)}` : 'non envoyé'}
                </Typography>
                <Typography variant="body2"><b>Essais d’envoi</b> : {c.tentatives}</Typography>
              </Grid>
            </Grid>
            {c.derniereErreur ? <Alert severity="warning" sx={{ mb: 2 }}>{c.derniereErreur}</Alert> : null}

            <Typography variant="subtitle2" gutterBottom>Adresse de livraison</Typography>
            {c.adresseEffaceeLe ? (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Effacée le {date(c.adresseEffaceeLe)} (90 jours après la fin).</Typography>
            ) : adresseEditee ? (
              <Box sx={{ mb: 2 }}>
                <FormulaireAdresse valeur={adresseEditee} onChange={setAdresseEditee} />
                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                  <Button size="small" onClick={() => setAdresseEditee(null)}>Annuler la saisie</Button>
                  <Button
                    size="small" variant="contained" disabled={occupe}
                    onClick={() => agir(() => AdminService.corrigerAdresseImpression(id, adresseEditee), () => 'Adresse enregistrée. « Relancer » pour envoyer la commande.')
                      .then((ok) => { if (ok) setAdresseEditee(null); })}
                  >
                    Enregistrer l’adresse
                  </Button>
                </Stack>
              </Box>
            ) : (
              <Box sx={{ mb: 2 }}>
                {c.adresse ? (
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                    {`${c.adresse.prenom} ${c.adresse.nom}\n${c.adresse.ligne1}${c.adresse.ligne2 ? `\n${c.adresse.ligne2}` : ''}\n${c.adresse.codePostal} ${c.adresse.ville} (${c.adresse.pays})\n${c.adresse.telephone} · ${c.adresse.email}`}
                  </Typography>
                ) : <Typography variant="body2" color="error">Aucune adresse : à saisir.</Typography>}
                {c.actions.corrigerAdresse ? (
                  <Button size="small" onClick={() => setAdresseEditee(c.adresse || {})}>Corriger l’adresse</Button>
                ) : null}
              </Box>
            )}

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom>Historique</Typography>
            <Table size="small">
              <TableBody>
                {[...(c.historique || [])].reverse().map((h, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <TableRow key={i}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{date(h.quand)}</TableCell>
                    <TableCell>{h.quoi}{h.detail ? ` — ${h.detail}` : ''}</TableCell>
                    <TableCell sx={{ color: 'text.secondary' }}>{h.par || ''}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
        {c?.actions.relancer ? (
          <Button disabled={occupe} onClick={() => agir(() => AdminService.relancerImpression(id), () => 'Envoi lancé : le rendu du PDF prend jusqu’à une minute. « Rafraîchir » pour voir l’issue.')}>Relancer</Button>
        ) : null}
        {c?.actions.actualiser ? (
          <Button disabled={occupe} onClick={() => agir(() => AdminService.actualiserImpression(id), (r) => `Chez Cloudprinter : ${r.cloudprinter.etat}${r.cloudprinter.articles.map((a) => ` · ${a.etat}${a.suivi ? ` (suivi ${a.suivi})` : ''}`).join('')}`)}>
            Relire chez Cloudprinter
          </Button>
        ) : null}
        {c?.actions.telecharger ? (
          <Button disabled={occupe} onClick={telecharger}>Télécharger le PDF d’impression</Button>
        ) : null}
        {c?.actions.annuler ? <Button color="warning" disabled={occupe} onClick={() => setDialogue('annuler')}>Annuler la commande</Button> : null}
        {c?.actions.refaire ? <Button color="secondary" disabled={occupe} onClick={() => setDialogue('refaire')}>Refaire un exemplaire</Button> : null}
        <Box sx={{ flex: 1 }} />
        <Button onClick={charger} disabled={occupe}>Rafraîchir</Button>
        <Button onClick={onFermer}>Fermer</Button>
      </DialogActions>

      <DialogueAction
        ouvert={dialogue === 'annuler'}
        titre="Annuler la commande"
        explication="Une commande déjà chez Cloudprinter ne s’annule que si la fabrication n’a pas commencé. Rien n’est remboursé ici : un remboursement éventuel se fait dans Stripe."
        libelleTexte="Raison (facultative : demande du client…)"
        onFermer={() => setDialogue(null)}
        onValider={(raison) => { setDialogue(null); agir(() => AdminService.annulerImpression(id, raison), () => 'Commande annulée.'); }}
      />
      <DialogueAction
        ouvert={dialogue === 'refaire'}
        titre="Refaire un exemplaire"
        explication="Une nouvelle commande chez Cloudprinter, du même Grimoire, facturée comme la première."
        libelleTexte="Motif (colis perdu, abîmé…)"
        texteObligatoire
        avecAdresse
        adresseInitiale={c?.adresse || null}
        onFermer={() => setDialogue(null)}
        onValider={(motif, adresse) => {
          setDialogue(null);
          agir(() => AdminService.refaireImpression(id, motif, adresse), (r) => `Exemplaire ${r.reference} créé et en cours d’envoi.`);
        }}
      />
    </Dialog>
  );
}

export default function ImpressionsScreen() {
  const [params, setParams] = useSearchParams();
  const [donnees, setDonnees] = useState(null);
  const [filtre, setFiltre] = useState(null);
  const [erreur, setErreur] = useState(null);
  const ouverte = params.get('commande');

  const charger = useCallback(async () => {
    try {
      setDonnees(await AdminService.getImpressions(filtre));
      setErreur(null);
    } catch (e) {
      setErreur(message(e, 'Lecture impossible'));
    }
  }, [filtre]);
  useEffect(() => { charger(); }, [charger]);

  const ouvrir = (id) => setParams(id ? { commande: String(id) } : {});
  const compteurs = donnees?.compteurs || {};
  const total = Object.values(compteurs).reduce((a, b) => a + b, 0);

  return (
    <AdminLayout>
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>Grimoires imprimés</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Les commandes chez Cloudprinter, du paiement à la livraison. Les alertes envoyées à l’équipe
          (envoi épuisé, adresse manquante, erreur ou annulation chez l’imprimeur, commande sans
          nouvelles) mènent ici. Les remboursements se font dans Stripe.
        </Typography>
        <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Chip label={`Toutes (${total})`} color={filtre ? 'default' : 'primary'} onClick={() => setFiltre(null)} />
          {ORDRE.filter((s) => compteurs[s]).map((s) => (
            <Chip
              key={s} label={`${STATUTS[s].libelle} (${compteurs[s]})`}
              color={filtre === s ? 'primary' : STATUTS[s].couleur} variant={filtre === s ? 'filled' : 'outlined'}
              onClick={() => setFiltre(s)}
            />
          ))}
        </Stack>
        {erreur ? <Alert severity="error" sx={{ mb: 2 }}>{erreur}</Alert> : null}
        {!donnees ? <CircularProgress /> : donnees.commandes.length === 0 ? (
          <Typography variant="body2" color="text.secondary">Aucune commande.</Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Référence</TableCell>
                  <TableCell>Statut</TableCell>
                  <TableCell>Enfant</TableCell>
                  <TableCell>Acheteur</TableCell>
                  <TableCell>Pays</TableCell>
                  <TableCell>Créée</TableCell>
                  <TableCell>Dernier signal</TableCell>
                  <TableCell>Suivi</TableCell>
                  <TableCell align="right">Coût HT</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {donnees.commandes.map((c) => (
                  <TableRow key={c.id} hover sx={{ cursor: 'pointer' }} onClick={() => ouvrir(c.id)}>
                    <TableCell>
                      {Object.keys(c.alertes || {}).length ? (
                        <Tooltip title={`Alertes : ${Object.keys(c.alertes).join(', ')}`}>
                          <WarningAmberIcon fontSize="small" color="warning" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                        </Tooltip>
                      ) : null}
                      {c.reference}{c.refaitDe ? ' (refait)' : ''}
                    </TableCell>
                    <TableCell>
                      <PuceStatut statut={c.statut} />
                      {c.derniereErreur ? (
                        <Typography variant="caption" color="error" display="block" sx={{ maxWidth: 260 }} noWrap title={c.derniereErreur}>
                          {c.derniereErreur}
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell>{c.prenomEnfant || '—'}</TableCell>
                    <TableCell>{c.acheteurEmail || '—'}</TableCell>
                    <TableCell>{c.pays || '—'}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{date(c.creeLe)}</TableCell>
                    <TableCell>{c.dernierSignal ? `${c.dernierSignal} (${date(c.dernierSignalLe)})` : '—'}</TableCell>
                    <TableCell>{c.suivi || '—'}</TableCell>
                    <TableCell align="right">{euros(c.coutHtCentimes)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {ouverte ? <DetailCommande id={ouverte} onFermer={() => ouvrir(null)} onChange={charger} /> : null}
      </Box>
    </AdminLayout>
  );
}
